import _, { intersection } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { fetchDashboardCommonFilters } from '../../api/reports';
import MultiSelect from '../../common/components/select/multi-select';
import { useSetLoading } from '../../common/hooks/appearance';
import { generateDashboardFilters, generateParentChildStatus } from '../../common/hooks/data';
import { apiErrorHandler } from '../../utils/handlers';
import { dashboardFiltersPrefixedData } from './utils/dashboardConstants';

const NewCommonFilters = ({ page }) => {
  const location = useLocation();
  const [filters, setFilters] = useState([]);
  const [initialRenderDone, setInitialRenderDone] = useState(false);
  const [commonFilters, setCommonFilters] = useState(dashboardFiltersPrefixedData);
  const [currentCommonFilters, setCurrentCommonFilters] = useState(dashboardFiltersPrefixedData);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState({
    entity: false,
    businessUnits: true,
    departments: true,
    salesManagers: true,
    unitManagers: true,
  });

  const filterChangeHandler = (type, value) => {
    const tempEntity = currentCommonFilters?.entity;
    let tempBU = currentCommonFilters?.businessUnits;
    let tempDept = currentCommonFilters?.departments;
    let tempSM = currentCommonFilters?.salesManagers;
    let tempUM = currentCommonFilters?.unitManagers;
    const tempFilters = { ...filters };
    tempFilters[type] = value;

    if (type === 'entity') {
      // Fetch BU w.r.t entity
      tempBU = commonFilters.businessUnits.filter((item) => {
        const commonEntity = intersection(item.supportedEntity, value);
        return commonEntity.length > 0;
      });
      tempBU = _.uniqBy(tempBU, 'value');
      tempFilters.businessUnits = tempBU.map((b) => b.value);

      // Fetch departments w.r.t entity
      tempDept = commonFilters.departments.filter((item) => {
        const commonEntity = intersection(item.supportedEntity, value);
        return commonEntity.length > 0;
      });
      tempDept = _.uniqBy(tempDept, 'value');
      tempFilters.departments = tempDept.map((d) => d.value);
    }

    if (type === 'departments') tempFilters.departments = value;
    if (['entity', 'departments'].includes(type)) {
      tempSM = commonFilters.salesManagers.filter((item) => {
        const commonSM = intersection(item.supportedDepartment, tempFilters.departments);
        const commonEntity = intersection(item.supportedEntity, tempFilters.entity);
        return commonSM.length > 0 && commonEntity.length > 0;
      });
      tempSM = _.uniqBy(tempSM, 'value');
      tempFilters.salesManagers = tempSM.map((s) => s.value);
    }

    if (['entity', 'departments', 'salesManagers'].includes(type)) {
      tempUM = commonFilters.unitManagers.filter((item) => {
        const commonUM = intersection(item.supportedSalesManager, tempFilters.salesManagers);
        return commonUM.length > 0;
      });
      tempUM = _.uniqBy(tempUM, 'value');
      tempFilters.unitManagers = tempUM.map((u) => u.value);
    }

    setCurrentCommonFilters({
      ...currentCommonFilters,
      entity: tempEntity,
      businessUnits: tempBU,
      departments: tempDept,
      salesManagers: tempSM,
      unitManagers: tempUM,
    });
    setFilters(tempFilters);
    setSelection({
      ...selection,
      businessUnits: tempBU.length <= 1,
      departments: tempDept.length <= 1,
      salesManagers: tempSM.length <= 1,
      unitManagers: tempUM.length <= 1,
    });

    if (type === 'entity' && value.length === 0) {
      const msg = 'Select at least one entity value(s).';
      const err = new Error(msg);
      err.response = { statusText: msg };
      apiErrorHandler(err);
    }
  };

  const handleFilters = (filterType, value) => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };

  const fetchCommonFiltersData = async () => {
    const commonFiltersData = { ...commonFilters };
    setLoading(true);
    try {
      const res = await fetchDashboardCommonFilters({
        ...(page === 'journals' && { journal: 'true' }),
        type: 'all',
      });
      const resData = res.data.data;
      // Entity Processing
      commonFiltersData.entity = [
        ...commonFiltersData.entity,
        ...resData.entity.map((item) => {
          return {
            label: item.name,
            value: item.id,
          };
        }),
      ];
      // Business Unit Processing
      commonFiltersData.businessUnits = [
        ...commonFiltersData.businessUnits,
        ...resData.businessUnits.map((item) => {
          return {
            label: item.name,
            value: item.id,
            supportedEntity: item.supportedEntity,
          };
        }),
      ];
      // Departments Processing
      commonFiltersData.departments = [
        ...commonFiltersData.departments,
        ...resData.departments.map((item) => {
          return {
            label: item.name,
            value: item.id,
            supportedEntity: item.supportedEntity,
          };
        }),
      ];
      // Sales Manager Processing
      commonFiltersData.salesManagers = [
        ...commonFiltersData.salesManagers,
        ...resData.salesManagers.map((item) => {
          return {
            label: item.name,
            value: item.id,
            supportedDepartment: item.supportedDepartment,
            supportedEntity: item.supportedEntity,
          };
        }),
      ];
      // Unit Manager Processing
      commonFiltersData.unitManagers = [
        ...commonFiltersData.unitManagers,
        ...resData.unitManagers.map((item) => {
          return {
            label: item.name,
            value: item.id,
            supportedSalesManager: item.supportedSalesManager,
          };
        }),
      ];
      // Store in Local CommonFilterState after Processing
      setCommonFilters(commonFiltersData);
      setCurrentCommonFilters(commonFiltersData);

      // Multi Select changes
      const currentFilters = {
        entity: commonFiltersData.entity.map((e) => e.value),
        businessUnits: commonFiltersData.businessUnits.map((e) => e.value),
        departments: commonFiltersData.departments.map((e) => e.value),
        salesManagers: commonFiltersData.salesManagers.map((e) => e.value),
        unitManagers: commonFiltersData.unitManagers.map((e) => e.value),
      };
      setInitialRenderDone(true);
      setSelection({
        ...selection,
        businessUnits: commonFilters.businessUnits.length > 0,
        departments: commonFilters.departments.length > 0,
        salesManagers: commonFilters.salesManagers.length > 0,
        unitManagers: commonFilters.unitManagers.length > 0,
      });
      if (
        location?.state?.globalFilters &&
        location?.state?.redirectionFrom === 'dashboard' &&
        !_.isEqual(location?.state?.globalFilters, dashboardFiltersPrefixedData)
      ) {
        const rFilters = location?.state?.globalFilters;
        const rFiltersOps = location?.state?.parentChildStatus;
        setFilters(rFilters);
        setCurrentCommonFilters(rFiltersOps);
        setSelection({
          ...selection,
          businessUnits: rFiltersOps.businessUnits.length <= 1,
          departments: rFiltersOps.departments.length <= 1,
          salesManagers: rFiltersOps.salesManagers.length <= 1,
          unitManagers: rFiltersOps.unitManagers.length <= 1,
        });
      } else {
        setFilters(currentFilters);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    fetchCommonFiltersData();
  }, []);

  useEffect(() => {
    if (initialRenderDone) {
      // Dispatching Common Filter State to Redux
      generateDashboardFilters(filters);
      generateParentChildStatus(currentCommonFilters);
    } else {
      generateDashboardFilters(dashboardFiltersPrefixedData);
    }
  }, [filters]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="styling">
      <Row className="entity-styling">
        <Col>
          <MultiSelect
            name="Entity"
            formInput={false}
            placeholder="Multiple Selections"
            label="ENTITY"
            onChange={(val) => {
              filterChangeHandler('entity', val);
            }}
            value={filters?.entity}
            options={currentCommonFilters?.entity}
            disableSearch
            disabled={selection.entity}
            disabledPlaceholder={
              selection.entity ? currentCommonFilters?.entity?.map((i) => i.label).join(',') : null
            }
          />
        </Col>
        <Col>
          <MultiSelect
            name="businessUnits"
            formInput={false}
            label="BUSINESS UNIT"
            placeholder="Multiple Selections"
            onChange={(val) => {
              handleFilters('businessUnits', val);
            }}
            value={filters?.businessUnits}
            options={currentCommonFilters?.businessUnits}
            disableSearch
            disabled={selection.businessUnits}
            disabledPlaceholder={
              selection.businessUnits
                ? currentCommonFilters?.businessUnits?.map((i) => i.label).join(',')
                : null
            }
          />
        </Col>
        <Col>
          <MultiSelect
            name="department"
            formInput={false}
            placeholder="Multiple Selections"
            label="Department"
            disabled={selection.departments}
            onChange={(val) => {
              filterChangeHandler('departments', val);
            }}
            value={filters?.departments}
            options={currentCommonFilters?.departments}
            disableSearch
            disabledPlaceholder={
              selection.departments
                ? currentCommonFilters?.departments?.map((i) => i.label).join(',')
                : null
            }
          />
        </Col>
        <Col>
          <MultiSelect
            name="salesManager"
            formInput={false}
            placeholder="Multiple Selections"
            label="Sales Manager"
            onChange={(val) => {
              filterChangeHandler('salesManagers', val);
            }}
            disabled={selection.salesManagers}
            value={filters?.salesManagers}
            options={currentCommonFilters?.salesManagers}
            disableSearch
            disabledPlaceholder={
              selection.salesManagers
                ? currentCommonFilters?.salesManagers?.map((i) => i.label).join(',')
                : null
            }
          />
        </Col>
        <Col>
          <MultiSelect
            name="unitManager"
            formInput={false}
            placeholder="Multiple Selections"
            label="Unit Manager"
            onChange={(val) => {
              handleFilters('unitManagers', val);
            }}
            disabled={selection.unitManagers}
            value={filters?.unitManagers}
            options={currentCommonFilters?.unitManagers}
            disableSearch
            disabledPlaceholder={
              selection.unitManagers
                ? currentCommonFilters?.unitManagers?.map((i) => i.label).join(',')
                : null
            }
          />
        </Col>
      </Row>
      <hr />
    </div>
  );
};

NewCommonFilters.propTypes = {
  page: PropTypes.string,
};

NewCommonFilters.defaultProps = {
  page: '',
};

export default NewCommonFilters;
